import { Component, computed, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';

import { CourseService } from '../../../api/course/course.service';
import { CourseCatalogueHeaderComponent } from './course-catalogue-header/course-catalogue-header.component';
import { CourseCatalogueDetailsCardComponent } from './course-catalogue-details-card/course-catalogue-details-card.component';
import { MatAccordion } from '@angular/material/expansion';
import { FaqEntry } from '../../faq/faq.interfaces';
import { campFaqData } from '../../faq/faq.data';
import { FaqEntryComponent } from '../../faq/faq-entry/faq-entry.component';
import { CurrencyPipe } from '@angular/common';
import { extractPeriodLabel } from '../../../util/date';
import { FrontendShareSocialsComponent } from '../../_layout/frontend-share-socials/frontend-share-socials.component';
import { canRegisterForCourse } from '../../../api/course/course.util';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AgesPipe } from '../../../util/courses/ages.pipe';
import { OvernightCoursePracticalExpositionComponent } from './exposition/overnight-course-practical-exposition/overnight-course-practical-exposition.component';
import { RegularCoursePracticalExpositionComponent } from './exposition/regular-course-practical-exposition/regular-course-practical-exposition.component';
import { OvernightCourseIncludedExpositionComponent } from './exposition/overnight-course-included-exposition/overnight-course-included-exposition.component';
import { RegularCourseIncludedExpositionComponent } from './exposition/regular-course-included-exposition/regular-course-included-exposition.component';
import { i18nRouterLinkDirective } from '../../../util/router/i18n-router-link.directive';

@Component({
  selector: 'app-course-catalogue-detail',
  standalone: true,
  imports: [
    CourseCatalogueHeaderComponent,
    MatIconModule,
    MatButtonModule,
    MatAccordion,
    MatTooltipModule,
    RouterModule,
    FaqEntryComponent,
    CurrencyPipe,
    AgesPipe,
    FrontendShareSocialsComponent,
    CourseCatalogueDetailsCardComponent,
    OvernightCoursePracticalExpositionComponent,
    RegularCoursePracticalExpositionComponent,
    OvernightCourseIncludedExpositionComponent,
    RegularCourseIncludedExpositionComponent,
    i18nRouterLinkDirective,
  ],
  templateUrl: './course-catalogue-detail.component.html',
})
export class CourseCatalogueDetailComponent {
  protected readonly resourceService = inject(CourseService);
  protected readonly route = inject(ActivatedRoute);
  protected readonly course = toSignal(
    this.route.params.pipe(
      switchMap((params) => this.resourceService.readWithCamp(params['id'])),
    ),
  );
  protected readonly label = computed(() => this.course()?.name ?? '');
  protected readonly period = computed(() =>
    extractPeriodLabel(this.course()?.camp.schedule),
  );
  protected readonly faqEntries: FaqEntry[] = campFaqData();

  isCourseAvailable = computed(() => {
    const course = this.course();
    return course && canRegisterForCourse(course);
  });
  tooltipText = computed(() => {
    return this.isCourseAvailable() ? '' : $localize`Registrations closed`;
  });
  conditionalText = computed(() => {
    const course = this.course();
    const conditionals: string[] = [];
    if (!course) return conditionals;
    if (course.camp.sleepover)
      conditionals.push($localize`This camp is with sleepover`);
    if ((course.minAge ?? 0) > 12)
      conditionals.push($localize`This camp is for teenagers`);
    if (course.camp.hostedFor)
      conditionals.push(
        $localize`This camp is in cooperation with ${course.camp.hostedFor?.name}`,
      );
    return conditionals;
  });

  scrollToSection($event: any) {
    const element = document.getElementById($event);
    if (element)
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
  }
}
